// src/components/FileVersionHistory.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as FileService from '../services/FileService.mjs';
import { useConfirm } from '../context/ConfirmContext.jsx';
import InlineBanner from './common/InlineBanner.jsx';
import ProgressBar from './common/ProgressBar.jsx';
import '../css/FileVersionHistory.css';

/**
 * Props:
 *   file              – the current File object (from FileDetailsModal state)
 *   onVersionUploaded – callback fired after a new version is successfully uploaded
 *   onVersionRestored – callback fired after an old version is restored
 *   onCountChange     – fired with the number of PREVIOUS versions after each load,
 *                       so a host (the details modal's tab strip) can label the
 *                       section without fetching the history a second time
 */
const FileVersionHistory = ({ file, onVersionUploaded, onVersionRestored, onCountChange }) => {
    const confirm = useConfirm();
    // ── State ──────────────────────────────────────────────────────────────────
    const [versions, setVersions]                 = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError]         = useState(null);

    const [isUploading, setIsUploading]           = useState(false);
    const [uploadProgress, setUploadProgress]     = useState(0);
    const [uploadError, setUploadError]           = useState(null);
    const [uploadSuccess, setUploadSuccess]       = useState(false);

    const [restoringId, setRestoringId]           = useState(null);
    const [historyExpanded, setHistoryExpanded]   = useState(false);

    const fileInputRef = useRef(null);

    // ── Load version history ───────────────────────────────────────────────────
    const loadVersions = useCallback(async () => {
        setIsLoadingHistory(true);
        setHistoryError(null);
        try {
            const data = await FileService.getFileVersions(file.id);
            data.sort((a, b) => b.versionNumber - a.versionNumber);
            setVersions(data);
            onCountChange?.(data.length);
        } catch (err) {
            console.error('Failed to load version history:', err);
            setHistoryError('Could not load version history.');
        } finally {
            setIsLoadingHistory(false);
        }
        // `onCountChange` must be referentially stable in the host (the details
        // modal memoizes it) — this callback drives the load effect below.
    }, [file.id, onCountChange]);

    useEffect(() => { loadVersions(); }, [loadVersions]);

    // ── Upload new version ─────────────────────────────────────────────────────
    const handleFileSelected = async (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;

        setIsUploading(true);
        setUploadProgress(0);
        setUploadError(null);
        setUploadSuccess(false);

        try {
            await FileService.uploadNewVersion(
                file.id, selected, false,
                (pct) => setUploadProgress(pct),
            );
            setUploadSuccess(true);
            setUploadProgress(100);
            await loadVersions();
            onVersionUploaded?.();
            setTimeout(() => setUploadSuccess(false), 3000);
        } catch (err) {
            console.error('Version upload failed:', err);
            setUploadError('Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // ── Restore an old version ─────────────────────────────────────────────────
    const handleRestore = async (versionId, versionNumber) => {
        const ok = await confirm({
            title: `Restore v${versionNumber}?`,
            message: 'The current version will be saved to history before restoring.',
            confirmText: 'Restore',
            cancelText: 'Cancel',
        });
        if (!ok) return;
        setRestoringId(versionId);
        try {
            await FileService.restoreFileVersion(file.id, versionId);
            await loadVersions();
            onVersionRestored?.();
        } catch (err) {
            // Surfaced globally by the axios interceptor (notification center).
            console.error('Restore failed:', err);
        } finally {
            setRestoringId(null);
        }
    };

    // ── Download an old version ────────────────────────────────────────────────
    const handleVersionDownload = async (versionId, fileName) => {
        try {
            const url = await FileService.buildVersionDownloadUrl(file.id, versionId);
            // fe-filebrowser-2: the `download` attribute is IGNORED for cross-origin
            // (signed GCS) URLs, so the file saved under the GCS object name instead
            // of `fileName`. Fetch the bytes and download via a same-origin blob URL
            // so the intended filename is honored.
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Download failed: ${res.status}`);
            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Defer revoke so the navigation has read the blob (mjs-svc-core-1).
            setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
        } catch (err) {
            console.error('Version download failed:', err);
        }
    };

    // ── Helpers ────────────────────────────────────────────────────────────────
    const formatDate = (raw) => {
        if (!raw) return '—';
        const date = raw._seconds ? new Date(raw._seconds * 1000) : new Date(raw);
        return date.toLocaleString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const formatDateShort = (raw) => {
        if (!raw) return '—';
        const date = raw._seconds ? new Date(raw._seconds * 1000) : new Date(raw);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / 86400000);
        // fe-filebrowser-6: a future timestamp (clock skew / bad data) made
        // diffDays negative → nonsensical "-3 days ago". Show the absolute date
        // for anything in the future instead.
        if (diffDays < 0)  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7)  return `${diffDays} days ago`;
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatSize = (bytes) => {
        if (!bytes) return '—';
        if (bytes < 1024)    return `${bytes} B`;
        if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1048576).toFixed(1)} MB`;
    };

    // Short hash-style ID for the "commit SHA" feel
    const shortId = (id) => id ? id.slice(0, 7) : '0000000';

    // ── Derived ────────────────────────────────────────────────────────────────
    const currentUploadedAt = file.metadata?.timeOfCreation;
    const olderCount = versions.length;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="fvh-root">

            {/* ══ Git-style commit timeline ══ */}
            <div className="fvh-timeline">

                {/* ── HEAD commit (current version) ── */}
                <div className="fvh-commit fvh-commit--head">
                    <div className="fvh-commit-spine">
                        <div className="fvh-commit-dot fvh-commit-dot--head" />
                        {/* Spine line continues downward only if there are older versions */}
                        {olderCount > 0 && <div className="fvh-commit-line" />}
                    </div>

                    <div className="fvh-commit-card fvh-commit-card--head">
                        <div className="fvh-commit-card-top">
                            <div className="fvh-commit-info">
                                <div className="fvh-commit-title-row">
                                    <span className="fvh-head-tag">HEAD</span>
                                    <span className="fvh-version-tag">v{file.versionNumber ?? 1}</span>
                                    <span className="fvh-commit-name">{file.metadata?.name}</span>
                                </div>
                                <div className="fvh-commit-sub">
                                    <span className="fvh-commit-sha" title={file.id}>{shortId(file.id)}</span>
                                    <span className="fvh-commit-sep">·</span>
                                    <span className="fvh-commit-size">{formatSize(file.metadata?.size)}</span>
                                    <span className="fvh-commit-sep">·</span>
                                    <span className="fvh-commit-date" title={formatDate(currentUploadedAt)}>
                                        {formatDateShort(currentUploadedAt)}
                                    </span>
                                </div>
                            </div>

                            <div className="fvh-commit-actions">
                                {/* Upload new version */}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelected}
                                    disabled={isUploading}
                                />
                                <button
                                    className="fvh-btn fvh-btn--upload"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploading}
                                    title="Upload a new version"
                                >
                                    {isUploading
                                        ? <><span className="fvh-spinner" /> {uploadProgress}%</>
                                        : <><span className="fvh-btn-icon">↑</span> New version</>
                                    }
                                </button>

                                <button
                                    className="fvh-btn fvh-btn--ghost"
                                    onClick={loadVersions}
                                    disabled={isLoadingHistory}
                                    title="Refresh history"
                                >
                                    <span className={isLoadingHistory ? 'fvh-spin' : ''}>↻</span>
                                </button>
                            </div>
                        </div>

                        {/* Progress bar (upload) */}
                        {isUploading && (
                            <div className="fvh-progress-wrap">
                                <ProgressBar value={uploadProgress} max={100} />
                            </div>
                        )}
                        {uploadSuccess && (
                            <div className="fvh-msg-wrap">
                                <InlineBanner variant="success">New version uploaded successfully.</InlineBanner>
                            </div>
                        )}
                        {uploadError && (
                            <div className="fvh-msg-wrap">
                                <InlineBanner variant="danger" onDismiss={() => setUploadError(null)}>
                                    {uploadError}
                                </InlineBanner>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Older versions ── */}
                {isLoadingHistory && (
                    <div className="fvh-commit fvh-commit--ghost">
                        <div className="fvh-commit-spine">
                            <div className="fvh-commit-dot fvh-commit-dot--ghost" />
                        </div>
                        <p className="fvh-loading">Loading history…</p>
                    </div>
                )}

                {historyError && (
                    <div className="fvh-msg-wrap">
                        <InlineBanner variant="danger" onDismiss={() => setHistoryError(null)}>
                            {historyError}
                        </InlineBanner>
                    </div>
                )}

                {!isLoadingHistory && versions.length === 0 && (
                    <div className="fvh-empty-hint">
                        No previous versions yet — upload a new version to start the history.
                    </div>
                )}

                {!isLoadingHistory && versions.length > 0 && (
                    <>
                        {/* "… and N older versions" collapse toggle */}
                        {!historyExpanded && (
                            <button
                                className="fvh-expand-btn"
                                onClick={() => setHistoryExpanded(true)}
                            >
                                <span className="fvh-expand-dots">•••</span>
                                {versions.length === 1
                                    ? 'Show 1 older version'
                                    : `Show ${versions.length} older versions`}
                            </button>
                        )}

                        {/* Older version commits */}
                        {historyExpanded && versions.map((v, idx) => {
                            const isLast = idx === versions.length - 1;
                            return (
                                <div key={v.id} className="fvh-commit fvh-commit--old">
                                    <div className="fvh-commit-spine">
                                        <div className="fvh-commit-dot fvh-commit-dot--old" />
                                        {!isLast && <div className="fvh-commit-line" />}
                                    </div>

                                    <div className="fvh-commit-card">
                                        <div className="fvh-commit-card-top">
                                            <div className="fvh-commit-info">
                                                <div className="fvh-commit-title-row">
                                                    <span className="fvh-version-tag fvh-version-tag--old">v{v.versionNumber}</span>
                                                    <span className="fvh-commit-name">{v.metadata?.name}</span>
                                                </div>
                                                <div className="fvh-commit-sub">
                                                    <span className="fvh-commit-sha" title={v.id}>{shortId(v.id)}</span>
                                                    <span className="fvh-commit-sep">·</span>
                                                    <span className="fvh-commit-size">{formatSize(v.metadata?.size)}</span>
                                                    <span className="fvh-commit-sep">·</span>
                                                    <span className="fvh-commit-date" title={formatDate(v.createdAt)}>
                                                        {formatDateShort(v.createdAt)}
                                                    </span>
                                                    {v.uploadedBy && (
                                                        <>
                                                            <span className="fvh-commit-sep">·</span>
                                                            <span className="fvh-commit-author" title="Uploaded by">
                                                                {v.uploadedBy}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="fvh-commit-actions">
                                                <button
                                                    className="fvh-btn fvh-btn--ghost"
                                                    onClick={() => handleVersionDownload(v.id, v.metadata?.name)}
                                                    title="Download this version"
                                                >
                                                    ↓
                                                </button>
                                                <button
                                                    className="fvh-btn fvh-btn--restore"
                                                    onClick={() => handleRestore(v.id, v.versionNumber)}
                                                    disabled={restoringId === v.id}
                                                    title="Restore this version"
                                                >
                                                    {restoringId === v.id
                                                        ? <><span className="fvh-spinner" /> Restoring…</>
                                                        : '⟳ Restore'
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Collapse button at the bottom when expanded */}
                        {historyExpanded && (
                            <button
                                className="fvh-expand-btn fvh-expand-btn--collapse"
                                onClick={() => setHistoryExpanded(false)}
                            >
                                ↑ Collapse history
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default FileVersionHistory;
